import React, { useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthContext from "../AuthContext";
import "./LandingPage.css";
import { Helmet } from "react-helmet-async";

const Icon = ({ children, size = 36 }) => (
    <div className="landing-icon" style={{ width: size, height: size }}>
        {children}
    </div>
);

const StockIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 3h18v4H3V3z" fill="#8A17D1" />
        <path d="M5 11h14v10H5V11z" fill="#D326AD" />
    </svg>
);

const SalesIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 21h18v-2H3v2z" fill="#8A17D1" />
        <path d="M7 7h10v10H7V7z" fill="#D326AD" />
    </svg>
);

const VendorIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="8" r="3" fill="#D326AD" />
        <path d="M5 20c1-4 6-6 7-6s6 2 7 6H5z" fill="#8A17D1" />
    </svg>
);

const ReportIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="16" rx="2" fill="#8A17D1" />
        <path d="M7 10h3v6H7v-6zM11 7h3v9h-3V7zM15 12h3v4h-3v-4z" fill="#D326AD" />
    </svg>
);

const PlanCard = ({ title, price, features, accent }) => (
    <div className={`landing-plan-card plan-${title.toLowerCase()} ${accent === "#374151" ? "accent-dark" : accent === "#111827" ? "accent-darker" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            <strong style={{ fontSize: 18 }}>{title}</strong>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#8A17D1" }}>{price}</div>
        <ul style={{ textAlign: "left", paddingLeft: 18 }}>
            {features.map((f, i) => (
                <li key={i}>{f}</li>
            ))}
        </ul>
        <Link to="/register" className="landing-primary-button-small">Get Started</Link>
    </div>
);

const LandingPage = () => {
    const auth = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (auth?.user) {
            // Don't redirect superadmin users to /app
            const isSuper = localStorage.getItem("isSuperAdmin") === "true";
            if (isSuper) {
                navigate("/superadmin", { replace: true });
            } else {
                navigate("/app", { replace: true });
            }
        }
    }, [auth, navigate]);

    return (
        <div className="landing-page">
            <header className="landing-header">
                <div className="landing-header-inner">
                    <div className="landing-brand-row">
                        <div className="landing-logo-mark">
                            <img src={require("../assets/stokraze.png")} alt="Stokraze Inventory Management System Logo" />
                        </div>
                        <div className="landing-brand">Inventory</div>
                    </div>
                    <nav className="landing-nav" style={{ flexWrap: 'wrap', gap: 8 }}>
                        <a href="#features" className="landing-nav-link">Features</a>
                        <a href="#plans" className="landing-nav-link">Plans</a>
                        <a href="#about" className="landing-nav-link">About</a>
                        <a href="#contact" className="landing-nav-link">Contact</a>
                        <Link to="/login" className="landing-link-button">Login</Link>
                        <Link to="/register" className="landing-primary-button">Sign Up</Link>
                    </nav>
                </div>
            </header>

            <main className="landing-main">
                <section className="landing-hero">
                    <div className="landing-hero-inner">
                        <div className="landing-hero-text">
                            <h1 className="landing-hero-title">  Smart Inventory Management System for Small Businesses</h1>
                            <p className="landing-hero-subtitle">
                                Stokraze helps small businesses track inventory, manage stock levels, 
                                monitor sales, and control purchases in one simple system.
                            </p>
                            <div style={{ marginTop: 20 }}>
                                <Link to="/register" className="landing-primary-button-large">Create Account</Link>
                                <Link to="/login" className="landing-link-button-alt">Login</Link>
                            </div>
                        </div>
                        <div className="landing-hero-art" aria-hidden>
                            <svg width="360" height="220" viewBox="0 0 720 440" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                    <linearGradient id="g" x1="0" x2="1">
                                        <stop offset="0%" stopColor="#8A17D1" />
                                        <stop offset="60%" stopColor="#D326AD" />
                                    </linearGradient>
                                </defs>
                                
                                {/* Main dashboard container */}
                                <rect x="40" y="20" width="640" height="400" rx="16" fill="rgba(138, 23, 209, 0.08)" stroke="url(#g)" strokeWidth="1.5" opacity="0.4" />
                                
                                {/* Header bar */}
                                <rect x="40" y="20" width="640" height="50" rx="16" fill="rgba(138, 23, 209, 0.15)" />
                                <circle cx="70" cy="45" r="12" fill="#D326AD" opacity="0.7" />
                                <rect x="95" y="38" width="120" height="8" rx="4" fill="#8A17D1" opacity="0.6" />
                                <rect x="570" y="35" width="80" height="20" rx="6" fill="#D326AD" opacity="0.5" />
                                
                                {/* Left chart panel */}
                                <rect x="60" y="90" width="280" height="160" rx="12" fill="rgba(211, 38, 173, 0.08)" border="1px solid rgba(138, 23, 209, 0.15)" />
                                
                                {/* Small bar chart */}
                                <rect x="80" y="270" width="20" height="35" rx="3" fill="#8A17D1" opacity="0.9" />
                                <rect x="110" y="280" width="20" height="25" rx="3" fill="#D326AD" opacity="0.8" />
                                <rect x="140" y="260" width="20" height="45" rx="3" fill="#8A17D1" opacity="0.85" />
                                <rect x="170" y="275" width="20" height="30" rx="3" fill="#D326AD" opacity="0.75" />
                                <rect x="200" y="250" width="20" height="55" rx="3" fill="#8A17D1" opacity="0.9" />
                                <rect x="230" y="285" width="20" height="20" rx="3" fill="#D326AD" opacity="0.7" />
                                
                                {/* Chart label */}
                                <text x="150" y="120" fontFamily="inter" fontSize="13" fill="#E6EEF3" fontWeight="600" opacity="0.8">Stock Levels</text>
                                <text x="150" y="190" fontFamily="inter" fontSize="11" fill="#C7D2FE" opacity="0.6">Last 6 months</text>
                                
                                {/* Right stats panel */}
                                <rect x="380" y="90" width="280" height="160" rx="12" fill="rgba(211, 38, 173, 0.08)" />
                                
                                {/* Stat boxes */}
                                <rect x="400" y="110" width="110" height="50" rx="8" fill="rgba(138, 23, 209, 0.2)" />
                                <text x="410" y="128" fontFamily="inter" fontSize="11" fill="#C7D2FE" opacity="0.7">Total Items</text>
                                <text x="410" y="150" fontFamily="inter" fontSize="18" fill="#D326AD" fontWeight="700">2,450</text>
                                
                                <rect x="530" y="110" width="110" height="50" rx="8" fill="rgba(211, 38, 173, 0.2)" />
                                <text x="540" y="128" fontFamily="inter" fontSize="11" fill="#C7D2FE" opacity="0.7">Low Stock</text>
                                <text x="540" y="150" fontFamily="inter" fontSize="18" fill="#8A17D1" fontWeight="700">24</text>
                                
                                <rect x="400" y="170" width="110" height="50" rx="8" fill="rgba(138, 23, 209, 0.2)" />
                                <text x="410" y="188" fontFamily="inter" fontSize="11" fill="#C7D2FE" opacity="0.7">This Month</text>
                                <text x="410" y="210" fontFamily="inter" fontSize="18" fill="#D326AD" fontWeight="700">$8.2K</text>
                                
                                <rect x="530" y="170" width="110" height="50" rx="8" fill="rgba(211, 38, 173, 0.2)" />
                                <text x="540" y="188" fontFamily="inter" fontSize="11" fill="#C7D2FE" opacity="0.7">Vendors</text>
                                <text x="540" y="210" fontFamily="inter" fontSize="18" fill="#8A17D1" fontWeight="700">18</text>
                                
                                {/* Bottom activity panel */}
                                <rect x="60" y="310" width="600" height="140" rx="12" fill="rgba(211, 38, 173, 0.08)" />
                                
                                {/* Activity items */}
                                <rect x="90" y="335" width="8" height="8" rx="4" fill="#D326AD" opacity="0.8" />
                                <text x="110" y="346" fontFamily="inter" fontSize="12" fill="#E6EEF3" opacity="0.9" fontWeight="500">Product XYZ added to inventory</text>
                                <text x="110" y="362" fontFamily="inter" fontSize="10" fill="#C7D2FE" opacity="0.5">2 hours ago</text>
                                
                                <rect x="90" y="380" width="8" height="8" rx="4" fill="#8A17D1" opacity="0.8" />
                                <text x="110" y="391" fontFamily="inter" fontSize="12" fill="#E6EEF3" opacity="0.9" fontWeight="500">New order from Supplier ABC received</text>
                                <text x="110" y="407" fontFamily="inter" fontSize="10" fill="#C7D2FE" opacity="0.5">5 hours ago</text>
                            </svg>
                        </div>
                    </div>
                </section>

                <section id="features" className="landing-section landing-section-with-scroll">
                    <h2 className="landing-section-title">Key Features</h2>
                    <div className="landing-features-grid">
                        <div className="landing-feature-card">
                            <div className="landing-feature-header"><Icon><StockIcon /></Icon><strong>Stock tracking</strong></div>
                            <p>Monitor quantities, receive low-stock alerts and track product variants.</p>
                        </div>
                        <div className="landing-feature-card">
                            <div className="landing-feature-header"><Icon><SalesIcon /></Icon><strong>Sales & Purchases</strong></div>
                            <p>Record sales, manage purchases and view margins.</p>
                        </div>
                        <div className="landing-feature-card">
                            <div className="landing-feature-header"><Icon><VendorIcon /></Icon><strong>Vendors & Customers</strong></div>
                            <p>Maintain vendor and customer records with contact history.</p>
                        </div>
                        <div className="landing-feature-card">
                            <div className="landing-feature-header"><Icon><ReportIcon /></Icon><strong>Reports</strong></div>
                            <p>Generate inventory and sales reports for better decisions.</p>
                        </div>
                    </div>
                </section>

                <section id="plans" className="landing-section landing-section-with-scroll">
                    <h2 className="landing-section-title">Plans</h2>
                    <div className="landing-plans-grid">
                        <PlanCard title="Basic" price="Free" features={["Single user", "10 items Limit", "Community support"]} accent="#374151" />
                        <PlanCard title="Classic" price="₦1,000/mo" features={["Admin and single user", "50 items limit", "Priority support"]} accent="#111827" />
                        <PlanCard
                            title="Premium"
                            price="₦2,000/mo"
                            features={[
                                "Admin and multiple users",
                                "Unlimited inventory items",
                                "Stokraze AI Assistant",
                                "AI stock alerts",
                                "Sales forecast",
                                "Business health score",
                                "AI business performance review",
                                "Natural-language reports",
                                "CSV report download",
                                "Dedicated support"
                            ]}
                            accent="#111827"
                        />
                    </div>
                </section>

                <section id="about" className="landing-section landing-section-with-scroll">
                    <h2 className="landing-section-title">About Us</h2>
                    <div style={{ maxWidth: 900, margin: "0 auto", color: "#C7D2FE", lineHeight: 1.8 }}>
                        <p>
                            Stokraze was born from a simple observation: small business owners spend too much time managing inventory spreadsheets and not enough time growing their businesses. We set out to change that.
                        </p>
                        <p style={{ marginTop: 16 }}>
                            Our team combines years of retail operations experience with modern software development expertise. We've worked in fast-paced retail environments, managed complex supply chains, and understand the real challenges that small to medium-sized businesses face when tracking stock, managing purchases, and maintaining vendor relationships.
                        </p>
                        <p style={{ marginTop: 16 }}>
                            <strong style={{ color: "#E6EEF3" }}>Our Mission:</strong> To empower small business owners with elegant, affordable tools that simplify inventory management and unlock time for what matters most-growing their business.
                        </p>
                        <p style={{ marginTop: 16 }}>
                            <strong style={{ color: "#E6EEF3" }}>What We Believe:</strong> Great software should be intuitive, not complicated. It should work as hard as you do. Stokraze is built on these principles-lightweight, reliable, and designed to scale with your business from day one.
                        </p>
                        <p style={{ marginTop: 16 }}>
                            Whether you're running a retail shop, managing a warehouse, or handling vendor operations, Stokraze adapts to your needs. We're committed to continuous improvement and listen to our users to deliver features that matter.
                        </p>
                    </div>
                </section>

                <section id="contact" className="landing-section landing-section-with-scroll" style={{ paddingBottom: 60 }}>
                    <h2 className="landing-section-title">Contact</h2>
                    <p style={{ color: "#334155" }}>If you have questions or want a demo, reach out to us.</p>
                    <div className="landing-contact-row">
                        <div className="landing-contact-card">
                            <strong>Email</strong>
                            <p><a href="mailto:stokrazeinventory@riantotechnologies.com" style={{ color: "#93C5FD" }}>stokraze@gmail.com</a></p>
                        </div>
                         <div className="landing-contact-card">
                            <strong>Phone</strong>
                            <p><a href="tel:+2348032466818" style={{ color: "#93C5FD" }}>+234 803 246 6818</a></p>
                        </div>
                        <div className="landing-contact-card">
                            <strong>Address</strong>
                            <p>Ikeja Lagos</p>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="landing-footer">
                <div>© {new Date().getFullYear()} Stokraze. All rights reserved.</div>
            </footer>
        </div>
    );
};

export default LandingPage;
